import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import { Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';

export interface IPurchaseOrder {
  id: number;
  supplierTPIN: string;
  supplierName: string;
  invoiceNumber: string;
  invoiceDate: string;
  goodsDescription: string;
  taxableAmount: number;
  total: number;
  vatCharged: number;
  state: number;
  itemsCount: number;
}

export interface IZraPurchasesResponse {
  errors: any[];
  message: string;
  succeeded: boolean;
  data: IPurchaseOrder[];
}

export interface IPurchaseOrderItem {
  id?: string;
  purchaseOrderId: string;
  productId: string;
  productName?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface IPurchaseOrderFilter {
  status?: string;
  supplierId?: string;
  locationId?: string;
  startDate?: Date;
  endDate?: Date;
  searchTerm?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PurchasesService {
  private base: string;
  private version: string;

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  /**
   * Log request details
   */
  private logRequest(method: string, endpoint: string, params?: any): void {
    console.log(`[Purchases Service] ${method} Request:`, {
      endpoint,
      timestamp: new Date().toISOString(),
      params: params || 'No params',
    });
  }

  /**
   * Log response details
   */
  private logResponse(method: string, endpoint: string, response: any): void {
    console.log(`[Purchases Service] ${method} Response:`, {
      endpoint,
      timestamp: new Date().toISOString(),
      response,
    });
  }

  /**
   * Get ZRA purchase orders/invoices summary
   * Calls: GET /api/v{version}/{entityId}/purchases/getAllPurchases
   */
  getPurchaseOrders(
    entityId: string,
    filters?: IPurchaseOrderFilter
  ): Observable<IZraPurchasesResponse> {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/purchases/getAllPurchases`;
    
    let params = new HttpParams();

    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.supplierId) params = params.set('supplierId', filters.supplierId);
    if (filters?.locationId) params = params.set('locationId', filters.locationId);
    if (filters?.startDate) params = params.set('startDate', filters.startDate.toISOString());
    if (filters?.endDate) params = params.set('endDate', filters.endDate.toISOString());
    if (filters?.searchTerm) params = params.set('searchTerm', filters.searchTerm);

    this.logRequest('GET', endpoint, params.toString());

    return this.httpClient
      .get<IZraPurchasesResponse>(endpoint, { params })
      .pipe(
        tap((response) => this.logResponse('GET', endpoint, response)),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Get purchase orders by location
   */
  getPurchaseOrdersByLocation(
    entityId: string,
    locationId: string,
    filters?: IPurchaseOrderFilter,
    pageNumber: number = 1,
    pageSize: number = 10
  ): Observable<IZraPurchasesResponse> {
    const updatedFilters = { ...filters, locationId };
    return this.getPurchaseOrders(entityId, updatedFilters);
  }

  /**
   * Get a specific purchase order/invoice by ID
   */
  getPurchaseOrder(entityId: string, purchaseId: number): Observable<IPurchaseOrder> {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/purchases/${purchaseId}`;
    this.logRequest('GET', endpoint);

    return this.httpClient.get<IPurchaseOrder>(endpoint).pipe(
      tap((response) => this.logResponse('GET', endpoint, response)),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Get purchase order details with items
   */
  getPurchaseOrderDetails(entityId: string, purchaseId: number): Observable<any> {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/purchases/${purchaseId}/details`;
    this.logRequest('GET', endpoint);

    return this.httpClient.get(endpoint).pipe(
      tap((response) => this.logResponse('GET', endpoint, response)),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Accept a ZRA purchase order
   * Calls: POST /api/v{version}/{entityId}/purchases/accept
   */
  approvePurchaseOrder(entityId: string, purchaseId: string, remarks: string = 'Accepted'): Observable<any> {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/purchases/accept`;
    const payload = { purchaseId, remarks };
    this.logRequest('POST', endpoint, payload);

    return this.httpClient.post<any>(endpoint, payload).pipe(
      tap((response) => this.logResponse('POST', endpoint, response)),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Reject a ZRA purchase order
   * Calls: POST /api/v{version}/{entityId}/purchases/reject
   */
  rejectPurchaseOrder(
    entityId: string,
    purchaseId: string,
    reason: string = 'Rejected'
  ): Observable<any> {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/purchases/reject`;
    const payload = { purchaseId, reason };
    this.logRequest('POST', endpoint, payload);

    return this.httpClient.post<any>(endpoint, payload).pipe(
      tap((response) => this.logResponse('POST', endpoint, response)),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Search purchase orders
   */
  searchPurchaseOrders(
    entityId: string,
    searchTerm: string,
    pageNumber: number = 1,
    pageSize: number = 10
  ): Observable<IZraPurchasesResponse> {
    return this.getPurchaseOrders(entityId, { searchTerm });
  }

  /**
   * Export purchase orders to Excel
   */
  exportPurchaseOrders(entityId: string, filters?: IPurchaseOrderFilter): Observable<Blob> {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/purchases/export`;
    
    let params = new HttpParams().set('export', 'excel');

    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.locationId) params = params.set('locationId', filters.locationId);
    if (filters?.startDate) params = params.set('startDate', filters.startDate.toISOString());
    if (filters?.endDate) params = params.set('endDate', filters.endDate.toISOString());

    this.logRequest('GET', endpoint, params.toString());

    return this.httpClient
      .get(endpoint, { params, responseType: 'blob' })
      .pipe(
        tap((response) =>
          this.logResponse('GET', endpoint, {
            type: 'Blob',
            size: response.size,
          })
        ),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }
}

